import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  BarChart3,
  Building2,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  FlaskConical,
  MapPinned,
  Tags,
  Contact,
  FileSignature,
  Handshake,
  FileText,
  Kanban,
  LayoutDashboard,
  Mail,
  Package,
  Settings,
  Target,
  UserPlus,
  Users,
  Phone,
} from "lucide-react";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapObject } from "@/lib/api";
import { useVisitSettings } from "@/lib/visits";
import { unwrapPage } from "@/lib/paging";

type IconType = React.ComponentType<{ className?: string }>;

/** The WhatsApp mark in the colour of the surrounding text, like the other line icons */
const WhatsApp: IconType = ({ className }) => <WhatsAppIcon color="currentColor" className={className} />;

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: IconType;
  /** Paths (and their children) that light this item up */
  match: string[];
  /** Waiting items shown as a gold count; hover text says what they are */
  badge: number;
  badgeTitle?: string;
}

export interface NavGroup {
  key: string;
  /** Heading shown above the group when the sidebar is open */
  title: string;
  items: NavItem[];
}

export interface PanelLink {
  label: string;
  to: string;
}

export interface PanelSection {
  title?: string;
  links: PanelLink[];
}

/** Modules shown on the sidebar until a person picks their own (the rest are under More) */
export const DEFAULT_PINS = ["home", "tasks", "leads", "deals", "quotes", "accounts", "whatsapp", "email"];
export const MAX_PINS = 10;

export function pathMatches(pathname: string, prefixes: string[]) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Everything the app frame needs to draw navigation, for the signed-in user */
export function useNav() {
  const { hasPermission, user } = useAuth();
  const { pathname, search } = useLocation();
  const isSuperAdmin = String(user?.UserRole?.RoleCode || "").toUpperCase() === "SUPER_ADMIN";

  const can = {
    leads: hasPermission("lead.read"),
    deals: hasPermission("deal.read"),
    accounts: hasPermission("account.read"),
    contacts: hasPermission("contact.read"),
    reports: hasPermission("report.view_own"),
    targets: hasPermission("target.read"),
    products: hasPermission("product.read"),
    quotes: hasPermission("quote.read"),
    orders: hasPermission("order.read"),
    priceLists: hasPermission("price_list.manage"),
    samples: hasPermission("sample.read"),
    visits: hasPermission("visit.read"),
    approveQuotes: hasPermission("quote.approve"),
    proposals: hasPermission("proposal.read"),
    agreements: hasPermission("agreement.read"),
    reviewProposals: hasPermission("proposal.approve"),
    activities: hasPermission("activity.read"),
    calendar: hasPermission("calendar.read") || hasPermission("activity.read"),
    emails: hasPermission("email.read"),
    whatsapp: hasPermission("whatsapp.read"),
    users: hasPermission("user.manage"),
    roles: hasPermission("role.manage"),
    settings: hasPermission("setting.manage"),
    whatsappAdmin: hasPermission("whatsapp.admin"),
    callAdmin: hasPermission("call.admin"),
    callMake: hasPermission("call.make"),
    callHistory: hasPermission("call.history"),
    leadSources: hasPermission("lead.source_manage") || isSuperAdmin,
    proposalTemplates: hasPermission("proposal_template.manage"),
    agreementTemplates: hasPermission("agreement_template.manage"),
  };

  // Companies whose reps don't visit customers switch visits off, and the menu hides them
  const visitSettings = useVisitSettings(can.visits);

  // Counts of things waiting for this user, refreshed on navigation
  const [quotesToApprove, setQuotesToApprove] = useState(0);
  useEffect(() => {
    if (!can.approveQuotes) return;
    api
      .get("/api/Quotation/List?mine_to_approve=true&page_size=1")
      .then((res) => setQuotesToApprove(unwrapPage(res).total))
      .catch(() => setQuotesToApprove(0));
  }, [can.approveQuotes, pathname]);

  const [proposalsToReview, setProposalsToReview] = useState(0);
  useEffect(() => {
    if (!can.reviewProposals) return;
    api
      .get("/api/Proposal/List?mine_to_review=true&page_size=1")
      .then((res) => setProposalsToReview(unwrapPage(res).total))
      .catch(() => setProposalsToReview(0));
  }, [can.reviewProposals, pathname]);

  const [tasksDue, setTasksDue] = useState(0);
  useEffect(() => {
    if (!can.activities) return;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    api
      .get(`/api/Task/Summary?from=${encodeURIComponent(start.toISOString())}&to=${encodeURIComponent(end.toISOString())}&scope=mine`)
      .then((res) => {
        const data = unwrapObject<{ overdue: number; today: number }>(res);
        setTasksDue((data.overdue || 0) + (data.today || 0));
      })
      .catch(() => setTasksDue(0));
  }, [can.activities, pathname]);

  const [emailWaiting, setEmailWaiting] = useState(0);
  useEffect(() => {
    if (!can.emails) return;
    api
      .get("/api/DealEmail/Counts")
      .then((res) => {
        const data = unwrapObject<{ unread: number; unlinked: number }>(res);
        setEmailWaiting((data.unread || 0) + (data.unlinked || 0));
      })
      .catch(() => setEmailWaiting(0));
  }, [can.emails, pathname, search]);

  const [whatsappUnread, setWhatsappUnread] = useState(0);
  useEffect(() => {
    if (!can.whatsapp) return;
    const load = () =>
      api
        .get("/api/WhatsApp/Counts")
        .then((res) => setWhatsappUnread(unwrapObject<{ unread: number }>(res).unread || 0))
        .catch(() => setWhatsappUnread(0));
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [can.whatsapp, pathname, search]);

  // Side panels for the two admin areas
  const settingsPanel = useMemo<PanelSection[]>(() => {
    const general: PanelLink[] = can.settings
      ? [
          { label: "Company", to: "/settings/company" },
          { label: "Currencies & countries", to: "/settings/currencies" },
          { label: "Payment terms", to: "/settings/payment-terms" },
          { label: "Pipelines", to: "/settings/pipelines" },
          { label: "Business rules", to: "/settings/rules" },
          { label: "Approvals", to: "/settings/approvals" },
          { label: "Notification rules", to: "/settings/notification-rules" },
          { label: "Master lookups", to: "/settings/masters" },
        ]
      : [];
    const security: PanelLink[] = isSuperAdmin
      ? [
          { label: "Sign-in security", to: "/settings/sign-in-security" },
          { label: "Security log", to: "/settings/security-log" },
        ]
      : [];
    const system: PanelLink[] = isSuperAdmin ? [{ label: "AI assistant", to: "/settings/ai" }, { label: "Storage", to: "/settings/storage" }] : [];
    const integrations: PanelLink[] = [
      ...(can.whatsappAdmin ? [{ label: "WhatsApp", to: "/settings/whatsapp" }] : []),
      ...(can.callAdmin ? [{ label: "Calling", to: "/settings/calling" }] : []),
      ...(can.settings ? [{ label: "Push notification setup", to: "/settings/push" }] : []),
      ...(can.leadSources ? [{ label: "Lead sources", to: "/settings/lead-sources" }] : []),
      ...(can.settings ? [{ label: "Quote design", to: "/settings/quote-design" }] : []),
      ...(can.proposalTemplates ? [{ label: "Proposal templates", to: "/settings/proposal-templates" }] : []),
      ...(can.agreementTemplates ? [{ label: "Agreement templates", to: "/settings/agreement-templates" }] : []),
    ];
    // Everyone who can make calls can check their own calling
    const mine: PanelLink[] = [
      { label: "Preferences", to: "/settings/preferences" },
    ];
    return [
      { title: "My account", links: mine },
      { title: "General", links: general },
      { title: "Security", links: security },
      { title: "System", links: system },
      { title: "Integrations", links: integrations },
    ].filter((s) => s.links.length > 0);
  }, [can.settings, can.whatsappAdmin, can.callAdmin, can.callMake, can.leadSources, can.proposalTemplates, can.agreementTemplates, isSuperAdmin]);

  const usersPanel = useMemo<PanelSection[]>(() => {
    const links: PanelLink[] = [
      ...(can.users ? [{ label: "Users", to: "/users" }] : []),
      ...(can.roles ? [{ label: "Roles & permissions", to: "/roles" }] : []),
    ];
    return links.length ? [{ links }] : [];
  }, [can.users, can.roles]);

  const groups = useMemo<NavGroup[]>(() => {
    const item = (
      key: string,
      label: string,
      to: string,
      icon: IconType,
      show: boolean,
      extra: Partial<Pick<NavItem, "match" | "badge" | "badgeTitle">> = {},
    ): NavItem | null =>
      show ? { key, label, to, icon, match: extra.match || [to], badge: extra.badge || 0, badgeTitle: extra.badgeTitle } : null;

    const list: { key: string; title: string; items: (NavItem | null)[] }[] = [
      {
        key: "home",
        title: "Workspace",
        items: [
          item("home", "Home", "/dashboard", LayoutDashboard, true),
          item("tasks", "Tasks", "/tasks", CheckSquare, can.activities, { badge: tasksDue, badgeTitle: `${tasksDue} due today or overdue` }),
          item("calendar", "Calendar", "/calendar", CalendarDays, can.calendar),
        ],
      },
      {
        key: "sales",
        title: "Sales",
        items: [
          item("leads", "Leads", "/leads", UserPlus, can.leads),
          item("deals", "Sales pipeline", "/deals", Kanban, can.deals),
          item("quotes", "Quotes", "/quotes", FileText, can.quotes, { badge: quotesToApprove, badgeTitle: `${quotesToApprove} to approve` }),
          item("orders", "Orders", "/orders", ClipboardList, can.orders),
          item("samples", "Samples", "/samples", FlaskConical, can.samples),
          item("proposals", "Proposals", "/proposals", FileSignature, can.proposals, { badge: proposalsToReview, badgeTitle: `${proposalsToReview} to review` }),
          item("agreements", "Agreements", "/agreements", Handshake, can.agreements),
          item("products", "Products", "/products", Package, can.products),
          item("pricelists", "Price lists", "/price-lists", Tags, can.priceLists),
        ],
      },
      {
        key: "customers",
        title: "Customers",
        items: [item("accounts", "Accounts", "/accounts", Building2, can.accounts), item("contacts", "Contacts", "/contacts", Contact, can.contacts)],
      },
      {
        key: "engage",
        title: "Communicate",
        items: [
          item("whatsapp", "WhatsApp", "/whatsapp", WhatsApp, can.whatsapp, { badge: whatsappUnread, badgeTitle: `${whatsappUnread} unread chats` }),
          item("email", "Email", "/emails", Mail, can.emails, { badge: emailWaiting, badgeTitle: `${emailWaiting} unread or unlinked` }),
          item("calls", "Calls", "/calls", Phone, can.callHistory),
          item("visits", "Field visits", "/visits", MapPinned, can.visits && visitSettings?.enabled !== false),
        ],
      },
      {
        key: "insights",
        title: "Insights",
        items: [item("reports", "Reports", "/reports", BarChart3, can.reports), item("targets", "Targets", "/targets", Target, can.targets)],
      },
    ];
    return list.map((g) => ({ key: g.key, title: g.title, items: g.items.filter((i): i is NavItem => !!i) })).filter((g) => g.items.length > 0);
  }, [can, tasksDue, quotesToApprove, proposalsToReview, whatsappUnread, emailWaiting, visitSettings]);

  const admin = useMemo<NavItem[]>(() => {
    const out: NavItem[] = [];
    const usersTo = usersPanel[0]?.links[0]?.to;
    if (usersTo) out.push({ key: "users", label: "Users & roles", to: usersTo, icon: Users, match: ["/users", "/roles"], badge: 0 });
    const settingsTo = settingsPanel[0]?.links[0]?.to;
    if (settingsTo) out.push({ key: "settings", label: "Settings", to: settingsTo, icon: Settings, match: ["/settings"], badge: 0 });
    return out;
  }, [usersPanel, settingsPanel]);

  const panel: { title: string; sections: PanelSection[] } | null = pathMatches(pathname, ["/settings"])
    ? { title: "Settings", sections: settingsPanel }
    : pathMatches(pathname, ["/users", "/roles"])
      ? { title: "Users & roles", sections: usersPanel }
      : null;

  const isActive = (i: NavItem) => pathMatches(pathname, i.match) || (i.key === "home" && pathname === "/");

  return { groups, admin, panel, settingsPanel, usersPanel, isActive, can, isSuperAdmin };
}
